import differenceInDays from 'date-fns/differenceInDays'
import isAfter from 'date-fns/isAfter'
import isBefore from 'date-fns/isBefore'
import format from 'date-fns/format'

import { Types } from 'service-api'
import { getInvoiceDates } from './invoices'
import { i18WithDefault as t } from './locales'
import { joinPetNamesFromProfile } from './pets'

export const isOrderConfirmed = (order?: Types.OrderClientDto | Types.OrderAdminDto) =>
  order?.orderStatus === 'CONFIRMED'

export const getOrderDates = (
  order: Types.OrderClientDto | Types.OrderAdminDto
): { minDate: Date | null; maxDate: Date | null } => {
  let minDate: Date | null = null,
    maxDate: Date | null = null
  order.invoices?.forEach((invoice) => {
    const { startDate, endDate } = getInvoiceDates(invoice)
    if (!minDate || isBefore(startDate, minDate)) {
      minDate = startDate
    }
    if (!maxDate || isAfter(endDate, maxDate)) {
      maxDate = endDate
    }
  })
  return { minDate, maxDate }
}

export const getServiceDatesText = (order: Types.OrderClientDto) => {
  const { minDate, maxDate } = getOrderDates(order)
  if (!minDate || !maxDate) {
    return t('global.label.N/A')
  }
  if (order.primaryServiceType?.serviceType?.name === 'daycare') {
    return order.invoices?.length === 1 ? format(minDate, 'MMM d, yyyy') : t('global.label.many')
  }

  if (differenceInDays(maxDate, minDate) <= 1) {
    return format(minDate, 'MMM d, yyyy')
  } else {
    return `${format(minDate, 'MMM d')} - ${format(maxDate, 'MMM d')}`
  }
}

export const getReservationSummary = (invoice: Types.CustomInvoiceAdminDto) => {
  return (invoice.items || []).map((item) => `${item.qty} ${item.displayName}`).join(', ')
}

export const orderFormatPetNames = (order?: Types.OrderAdminDto) => {
  if (!order) {
    return ''
  }
  const profiles =
    order.pets
      ?.map((p) => (p.locationPetProfile ? p.locationPetProfile : { displayName: p.displayName }))
      ?.filter(Boolean) || []
  return joinPetNamesFromProfile(profiles)
}

export const recentTransactions = (order?: Types.OrderAdminDto) => {
  if (!order?.payments?.length) {
    return []
  }
  return order.payments
    .map((payment) => (payment.transactions || []).map((transaction) => ({ ...transaction, payment })))
    .flat()
    .sort((a, b) => (a.createdAt! < b.createdAt! ? 1 : a.createdAt! > b.createdAt! ? -1 : 0))
}

export const getOrderPrimaryServiceTypeName = (order?: Types.OrderAdminDto) =>
  order?.primaryServiceType?.serviceType?.name

export const isOvernightFromOrder = (order?: Types.OrderAdminDto) => !!order?.primaryServiceType?.serviceType?.overnight

export const isAdyenEnabled = (order?: Types.OrderAdminDto) => order?.location?.payFacConfig?.type === 'ADYEN'
